import type { CSSProperties } from 'react';

import { periodDates } from '../../MarketExplorer/format';
import {
  CheckStatus,
  CurveSeries,
  DataStatus,
  Gap,
  GapKind,
  GapOwner,
  Instrument,
  InstrumentKind,
  IsoDate,
  Move,
  MoveUnit,
  NewsRole,
  Overlap,
  PregradeStatus,
  Preview,
  ProposalWindow,
  RejectReason,
  SavedState,
  Sign,
} from './types';

/** Typographic minus, so a negative move reads as one in the cards. */
const MINUS = '−';

export const plural = (count: number, one: string, many = `${one}s`) =>
  `${count} ${count === 1 ? one : many}`;

const finite = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * A move in its own unit: bonds in whole basis points of yield; FX, ETFs,
 * equities, commodities and any other kind in percent (two decimals unless
 * told otherwise).
 */
export const formatMove = (
  value: number | null | undefined,
  unit: MoveUnit,
  percentDecimals = 2,
): string => {
  if (!finite(value)) return '—';
  if (unit === 'bp') {
    const rounded = Math.round(value);
    const sign = rounded > 0 ? '+' : rounded < 0 ? MINUS : '';
    return `${sign}${Math.abs(rounded)} bp`;
  }
  const fixed = Math.abs(value).toFixed(percentDecimals);
  const zero = Number(fixed) === 0;
  const sign = zero ? '' : value > 0 ? '+' : MINUS;
  return `${sign}${fixed}%`;
};

/** "EUR/USD −3.2% · US2Y +45 bp" */
export const movesLine = (moves: Move[]) =>
  moves
    .map(move => `${move.code} ${formatMove(move.value, move.unit, 1)}`)
    .join(' · ');

export const windowLine = (window: ProposalWindow) =>
  `${periodDates(window.start, window.end)} · ${plural(window.weeks, 'week')}`;

export const cadenceLabel = (days: number | null | undefined): string => {
  if (!finite(days) || days <= 0) return 'unknown cadence';
  if (days <= 1) return 'daily';
  if (days <= 8) return 'weekly';
  if (days <= 16) return 'fortnightly';
  if (days <= 35) return 'monthly';
  if (days <= 100) return 'quarterly';
  return `every ${Math.round(days)} days`;
};

export const overlapPercent = (fraction: number) =>
  `${Math.round(Math.max(0, fraction) * 100)}%`;

/** "overlaps THE BLIND 40% (monthly)", or "no overlap". */
export const overlapLine = (overlap: Overlap | null | undefined) =>
  overlap && overlap.fraction > 0
    ? `overlaps ${overlap.name} ${overlapPercent(overlap.fraction)} (${cadenceLabel(
        overlap.cadence_days,
      )})`
    : 'no overlap';

/** The z at which a weekly-grid cell reaches full colour. */
export const FULL_COLOUR_Z = 3;

/**
 * The weekly-grid cell shade, after the Benchmarks heatmap: green for a
 * positive move, red for a negative one, intensity by |z| (full at |z| = 3).
 * The text keeps its default colour for legibility.
 */
export const zCellStyle = (
  value: number | null | undefined,
  z: number | null | undefined,
): CSSProperties => {
  const base: CSSProperties = { textAlign: 'center', whiteSpace: 'nowrap' };
  if (!finite(value) || !finite(z) || value === 0) return base;
  const strength = Math.min(1, Math.abs(z) / FULL_COLOUR_Z);
  const alpha = Number((0.08 + strength * 0.77).toFixed(3));
  return {
    ...base,
    background:
      value > 0 ? `rgba(82, 196, 26, ${alpha})` : `rgba(255, 77, 79, ${alpha})`,
  };
};

export const REJECT_REASON_LABELS: Record<RejectReason, string> = {
  already_covered: 'Already covered',
  boring: 'Boring',
  wrong_period: 'Wrong period',
  weak_news: 'Weak news',
  other: 'Other',
};

export const ROLE_COLOURS: Record<NewsRole, string | undefined> = {
  driver: 'blue',
  context: undefined,
  counter: 'orange',
};

export const CHECK_STATUS_COLOURS: Record<CheckStatus, string> = {
  ok: 'green',
  warn: 'gold',
  fail: 'red',
};

export const PREGRADE_COLOURS: Record<PregradeStatus, string | undefined> = {
  not_predicted: undefined,
  pass: 'green',
  warn: 'gold',
  fail: 'red',
};

export const readable = (value: string) => value.replace(/_/g, ' ');

export const signArrow = (sign: Sign) =>
  sign > 0 ? '↑' : sign < 0 ? '↓' : '→';

export const signWord = (sign: Sign) =>
  sign > 0 ? 'up' : sign < 0 ? 'down' : 'no push';

/* ------------------------------------------------------------------ *
 * Instruments of any kind
 * ------------------------------------------------------------------ */

const KIND_LABELS: Record<string, string> = {
  fx: 'FX',
  bond: 'bond',
  etf: 'ETF',
  equity: 'equity',
  commodity: 'commodity',
  crypto: 'crypto',
};

/** "FX", "ETF", "bond"…; a kind this screen does not know yet reads as sent. */
export const kindLabel = (kind: InstrumentKind | null | undefined) =>
  kind ? KIND_LABELS[kind] || readable(String(kind)) : '';

/**
 * The unit an instrument moves in: a bond in bp of yield, every other kind
 * (an unknown one included) in percent of its price. The preview's own unit
 * wins when it is one of the two.
 */
export const moveUnitOf = (
  instrument: Pick<Instrument, 'kind' | 'unit'>,
): MoveUnit =>
  instrument.unit === 'bp' || instrument.unit === '%'
    ? instrument.unit
    : instrument.kind === 'bond'
      ? 'bp'
      : '%';

/* ------------------------------------------------------------------ *
 * Curves
 * ------------------------------------------------------------------ */

/**
 * Which axis a curve is drawn on. The preview says (`axis`); a series without
 * a known axis goes by its instrument: a bond is a yield in percent on the
 * right, every other kind a price on the left. A curve is never dropped for
 * an axis or kind this screen does not know.
 */
export const curveAxis = (
  series: Pick<CurveSeries, 'axis' | 'unit'>,
  kind?: InstrumentKind | null,
): 'price' | 'yield' => {
  const axis: string | undefined = series.axis;
  if (axis === 'yield' || axis === 'price') return axis;
  if (kind) return kind === 'bond' ? 'yield' : 'price';
  return series.unit === '%' ? 'yield' : 'price';
};

/** Price lines further apart in level than this are rebased by default. */
export const NORMALIZE_RATIO = 5;

const positive = (value: number | null | undefined): value is number =>
  finite(value) && value > 0;

/** A line's typical level: the median of its positive values; null without any. */
export const seriesLevel = (
  values: Array<number | null> | null | undefined,
) => {
  const sorted = (values || []).filter(positive).sort((a, b) => a - b);
  if (!sorted.length) return null;
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
};

/**
 * Whether price lines sit so far apart (USD/TRY ~30 beside EUR/USD ~1.1) that
 * on one raw axis the smaller reads flat: the highest typical level is more
 * than `ratio` times the lowest.
 */
export const levelsDiffer = (
  lines: Array<Array<number | null> | null | undefined>,
  ratio = NORMALIZE_RATIO,
) => {
  const levels = lines.map(seriesLevel).filter(positive);
  return levels.length > 1 && Math.max(...levels) / Math.min(...levels) > ratio;
};

/**
 * The index of the last date on or before `day` (ISO dates sort as text):
 * where a rebased line reads 100. 0 when every date is later.
 */
export const anchorIndex = (dates: IsoDate[], day: IsoDate) =>
  dates.reduce((found, date, index) => (date <= day ? index : found), 0);

/**
 * A price line rebased to 100 at `anchor`, so +3% since then reads 103. The
 * base is the last positive value at or before the anchor, else the first
 * after it; a line with no positive value cannot be rebased and comes back
 * empty rather than wrong.
 */
export const rebaseTo100 = (
  values: Array<number | null> | null | undefined,
  anchor = 0,
): Array<number | null> => {
  const list = values || [];
  const from = Math.min(Math.max(anchor, 0), list.length - 1);
  const before = list.slice(0, from + 1).filter(positive);
  const after = list.slice(from + 1).filter(positive);
  const base = before.length
    ? before[before.length - 1]
    : after.length
      ? after[0]
      : null;
  return list.map(value =>
    base != null && finite(value) ? (value / base) * 100 : null,
  );
};

/* ------------------------------------------------------------------ *
 * Data coverage
 * ------------------------------------------------------------------ */

export const DATA_STATUS_COLOURS: Record<DataStatus, string> = {
  complete: 'green',
  partial: 'gold',
  blocked: 'red',
};

/**
 * "Data complete", "Data gaps: 3 (none blocking)" or "Data gaps: 3 (1
 * blocking)". Null when the status is unknown (a version built before the data
 * check), so no badge claims a coverage nobody measured.
 */
export const dataBadgeLabel = (
  status: DataStatus | null | undefined,
  gapCount = 0,
  blockers = 0,
): string | null => {
  if (!status || !(status in DATA_STATUS_COLOURS)) return null;
  if (status === 'complete') return 'Data complete';
  return `Data gaps: ${gapCount} (${blockers > 0 ? blockers : 'none'} blocking)`;
};

export const GAP_KIND_LABELS: Record<GapKind, string> = {
  prices: 'prices',
  yields: 'yields',
  news: 'news',
  tradable: 'not tradable',
  grading: 'not graded',
  bond_issues: 'bond issues',
  archive: 'news archive',
};

export const GAP_OWNER_NOTES: Record<GapOwner, string> = {
  ours: 'Ours: the scenario builder, assessment, newsgraph or admin screens.',
  'PM (Tudor)': "PM (Tudor): another PM app's code or admin endpoint.",
  data: 'Data: an import or backfill task, no code change.',
};

export const isBlocker = (gap: Gap) => gap.severity === 'blocker';

/** Blockers first; otherwise the order the backend gave (a stable sort). */
export const sortGaps = (gaps: Gap[]) => [
  ...gaps.filter(isBlocker),
  ...gaps.filter(gap => !isBlocker(gap)),
];

/**
 * "T2", "T2–T4" or "T1, T3–T5": a gap on a long window can name many turns,
 * and consecutive runs read better as ranges.
 */
export const turnsLabel = (turns: number[] | null | undefined): string => {
  const sorted = Array.from(new Set(turns || []))
    .filter(turn => Number.isInteger(turn))
    .sort((a, b) => a - b);
  const runs: Array<[number, number]> = [];
  sorted.forEach(turn => {
    const last = runs[runs.length - 1];
    if (last && turn === last[1] + 1) last[1] = turn;
    else runs.push([turn, turn]);
  });
  return runs
    .map(([from, to]) => (from === to ? `T${from}` : `T${from}–T${to}`))
    .join(', ');
};

/**
 * How many AI Curated rows saving this preview writes: one per distinct news
 * item selected across its turns (the writer is keyed on the original, so an
 * item listed twice is still one row).
 */
export const curatedRowCount = (preview: Preview) =>
  new Set(preview.turns.flatMap(turn => turn.news.map(item => item.id))).size;

const AI_CURATED_TAG = "(tag 'AI Curated')";

/**
 * The save dialog's promise, before anything is written. The writer is
 * idempotent per original and wording, so a copy an earlier save already made
 * is reused rather than duplicated; the note says so instead of promising a
 * fresh row for each.
 */
export const curatedRowsNote = (count: number) =>
  count > 0
    ? `Saving creates ${plural(count, 'AI Curated news row')} ${AI_CURATED_TAG}, each linked to its original headline. A copy an earlier save already made with the same wording is reused, not duplicated. The originals are not changed.`
    : 'Saving creates no AI Curated news rows: this version selects no news.';

/** Whether a save reported excluded instruments or AI Curated rows. */
export const hasSavedExtras = (saved: SavedState | null | undefined) =>
  Boolean(
    saved &&
      ((saved.excluded_instruments || []).length > 0 ||
        typeof saved.curated_rows === 'number'),
  );

/** What a finished save reports. */
export const curatedRowsLine = (count: number) =>
  `${plural(count, 'AI Curated news row')} ${AI_CURATED_TAG}, each linked to its original headline.`;
